import React from 'react';
import { CheckSquare, Square, Loader2, AlertTriangle, Ban } from 'lucide-react';
import { JHA_PPE_GEAR, itemIdentity, type JhaCrewMemberLike, type JhaPpeCheck, type PpeLineItem } from '@/utils/jhaPpeGear';

/** UAT-008: why a tagged-out piece must not be treated as in service. */
function taggedOutReason(status: string | null | undefined): string | null {
  if (status === 'DAMAGED') return 'DAMAGED — tagged out of service. A qualified person must inspect and approve it before use.';
  if (status === 'MAINTENANCE') return 'IN MAINTENANCE — tagged out of service until returned.';
  if (status === 'RETIRED' || status === 'EXPIRED') return 'OUT OF SERVICE (retired/expired). Do not use.';
  return null;
}

/**
 * WD-T1 / v1.5 (Brian 2026-10-03, per-ITEM check-off): each piece of the
 * person's gear is its OWN line item — identified by model + serial (e.g.
 * "Miller H-500 · SN 12345") — and checking it off counts for ONLY that
 * piece (never fanned out to same-type gear). Untracked required gear shows
 * one fallback line per gear type (JHA-only evidence). Optional gear lines
 * (SRL, spreader bar, safety climb device — "if used / assigned") offer a
 * "Not used today" toggle instead. Required gear lines must be fully checked
 * (no waiver). Shared by CreateJHAPage and EditJHAPage.
 *
 * Line items come from GET /jha/ppe-items (the server's gear↔item resolver is
 * the single source of truth), so this component never guesses which item a
 * check-off covers.
 */
interface Props {
  member: JhaCrewMemberLike;
  /** The member's resolved line items; undefined = still loading/unavailable. */
  items?: PpeLineItem[];
  /** Non-empty when the gear list failed to load (why+how is shown). */
  itemsError?: string | null;
  onChange: (ppeChecks: JhaPpeCheck[]) => void;
}

export function JhaMemberPpeChecks({ member, items, itemsError, onChange }: Props) {
  const checks = member.ppeChecks || [];

  // A row belongs to ONE line item: item rows match their assetId, untracked
  // fallback lines match their gear. A legacy type-level row (pre-v1.5 draft,
  // no assetId) lights up the FIRST line of that gear and is rewritten as the
  // item's own row on the next toggle (same outcome the server auto-binds).
  // Sol MED-1: the legacy row binds to the FIRST item of the gear ONLY —
  // never to every same-type piece (the server rejects it as ambiguous when
  // 2+ items exist; the UI must not render contradictory completion).
  const firstItemOfGear = new Map<string, number>();
  for (const item of items || []) {
    if (item.assetId != null && !firstItemOfGear.has(item.gearId)) firstItemOfGear.set(item.gearId, item.assetId);
  }
  const rowFor = (item: PpeLineItem): JhaPpeCheck | undefined => {
    const exact = checks.find((c) =>
      c && (item.assetId != null ? c.assetId === item.assetId : c.assetId == null && c.gearId === item.gearId)
    );
    if (exact) return exact;
    return item.assetId != null && firstItemOfGear.get(item.gearId) === item.assetId
      ? checks.find((c) => c && c.assetId == null && c.gearId === item.gearId)
      : undefined;
  };

  const setCheck = (item: PpeLineItem, patch: Partial<JhaPpeCheck>) => {
    const existing = rowFor(item);
    const target: JhaPpeCheck = {
      ...(existing ?? { gearId: item.gearId }),
      ...patch,
      gearId: item.gearId,
      // Writing always names the item explicitly (v1.5 §5) — or drops the
      // field for untracked fallback lines.
      assetId: item.assetId ?? undefined,
    };
    if (target.assetId === undefined) delete (target as any).assetId;
    const next = existing ? checks.map((c) => (c === existing ? target : c)) : [...checks, target];
    onChange(next);
  };

  const togglePoint = (item: PpeLineItem, pointId: string) => {
    const check = rowFor(item) || { gearId: item.gearId, results: [] as any[] };
    const results = [...(check.results || [])];
    const idx = results.findIndex((r) => r.pointId === pointId);
    if (idx >= 0) results.splice(idx, 1);
    else results.push({ pointId, status: 'PASS' });
    setCheck(item, { notUsed: false, results });
  };

  return (
    <div className="mt-3 border-t pt-3" data-testid={`ppe-checks-${member.id}`}>
      <p className="text-sm font-semibold text-gray-900 mb-1">
        PPE inspection check-off <span className="font-normal text-gray-500">— this person checks off each piece of their own gear (model + serial); one check-off counts for one item</span>
      </p>
      {itemsError ? (
        <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-xs text-red-800 flex items-start gap-2" data-testid={`ppe-items-error-${member.id}`}>
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>
            Couldn’t load {member.name || 'this person'}’s gear list — each check-off must count for one specific
            item (model + serial), so the checklist needs that list. {itemsError} Reload the page to try again.
          </span>
        </div>
      ) : items === undefined ? (
        <p className="text-xs text-gray-500 flex items-center gap-2" data-testid={`ppe-items-loading-${member.id}`}>
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading this person’s gear list (model + serial)…
        </p>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {items.map((item) => {
            const gear = JHA_PPE_GEAR.find((g) => g.id === item.gearId)!;
            const rowKey = item.assetId != null ? `item-${item.assetId}` : gear.id;
            const check = rowFor(item);
            const notUsed = check?.notUsed === true;
            // UAT-008 (fail closed): a DAMAGED/tagged-out tracked piece is
            // labelled loudly and its inspection checklist is BLOCKED — the
            // same removal-from-service state the lift-plan picker enforces.
            // It can only be marked "Not used today".
            const outReason = item.assetId != null ? taggedOutReason(item.status) : null;
            const taggedOut = outReason !== null;
            const done = !taggedOut && gear.points.every((p) =>
              (check?.results || []).some((r) => r.pointId === p.id && (r.status === 'PASS' || r.status === 'FAIL'))
            );
            return (
              <div
                key={rowKey}
                className={`rounded-lg border p-3 ${taggedOut ? 'border-red-500 bg-red-50' : done ? 'border-green-300 bg-green-50' : notUsed ? 'border-gray-200 bg-gray-50' : 'border-amber-300 bg-amber-50'}`}
                data-testid={`ppe-item-${rowKey}-${member.id}`}
              >
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="text-sm font-medium text-gray-900">
                    {gear.label}
                    {item.assetId != null ? (
                      <span className="block text-xs font-normal text-gray-600">{itemIdentity(item)}</span>
                    ) : (
                      <span className="block text-xs font-normal text-gray-500">not tracked in the asset tracker</span>
                    )}
                    {!gear.required && (
                      <span className="ml-2 text-xs font-normal text-gray-500">only if used/assigned</span>
                    )}
                  </span>
                  {item.assetId != null && (
                    <label className="flex items-center gap-1 text-xs text-gray-700 whitespace-nowrap">
                      <input
                        type="checkbox"
                        data-testid={`not-used-${rowKey}-${member.id}`}
                        checked={notUsed}
                        onChange={(e) => setCheck(item, e.target.checked ? { notUsed: true, results: [] } : { notUsed: false, results: [] })}
                        className="w-4 h-4"
                      />
                      Not used today
                    </label>
                  )}
                </div>
                {taggedOut && (
                  <div
                    className="rounded border border-red-400 bg-white p-2 mb-2 flex items-start gap-2"
                    data-testid={`ppe-tagged-out-${rowKey}-${member.id}`}
                  >
                    <Ban className="h-4 w-4 mt-0.5 text-red-700 shrink-0" />
                    <span className="text-xs text-red-800 font-semibold">{outReason}</span>
                  </div>
                )}
                {!notUsed && !taggedOut && (
                  <div className="space-y-1">
                    {gear.points.map((point) => {
                      const checked = (check?.results || []).some(
                        (r) => r.pointId === point.id && (r.status === 'PASS' || r.status === 'FAIL')
                      );
                      return (
                        <label key={point.id} className="flex items-start gap-2 text-xs text-gray-700 cursor-pointer">
                          <input
                            type="checkbox"
                            data-testid={`ppe-point-${rowKey}-${point.id}-${member.id}`}
                            checked={checked}
                            onChange={() => togglePoint(item, point.id)}
                            className="mt-0.5 w-3.5 h-3.5"
                          />
                          <span>{point.label}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
                {notUsed && (
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <Square className="h-3 w-3" /> Marked not used today — no check-off required.
                  </p>
                )}
                {done && (
                  <p className="text-xs text-green-700 mt-2 flex items-center gap-1">
                    <CheckSquare className="h-3 w-3" /> All items checked off.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
